import { useMemo } from 'react';
import { useLoader } from '@/hooks/use-loader';
import { useCurrentProject } from '@/lib/current-project';
import { listProjects } from '@/lib/projects-api';
import { taskPath } from '@/navigation/project-paths';

export { hasTaskRefs, taskRefRanges, type TaskRefRange } from './task-refs';

/** A task ref's page (task-refs.ts finds them), or null for a prefix no project the reader can see has. */
export type TaskRefPath = (prefix: string, number: number) => string | null;

/**
 * Paths for task refs, from the projects the reader can see (the sidebar's list, loaded once and
 * shared), and the current project's own prefix while that list loads. Loads nothing when `enabled`
 * is false (text with no refs).
 */
export function useTaskRefPath(enabled: boolean): TaskRefPath {
  const current = useCurrentProject();
  const loader = useLoader((signal) => listProjects({ signal }), [], { key: 'factory:projects', enabled });
  const listed = loader.data?.data?.projects;
  return useMemo(() => {
    const slugs = new Map<string, string>();
    for (const p of listed ?? []) slugs.set(p.task_prefix, p.slug);
    if (current) slugs.set(current.task_prefix, current.slug);
    return (prefix, number) => {
      const slug = slugs.get(prefix);
      return slug ? taskPath(slug, `${prefix}-T-${number}`) : null;
    };
  }, [listed, current]);
}
